import React, { useLayoutEffect, useMemo, type ReactNode } from "react";
import { UiProvider, type UiProviderValue } from "@omega/ui";
import { renderOmpPickerFullscreen } from "./renderOmpPickerFullscreen";
import "./omega-ui.theme.css";

const THEME_ROOT_CLASSES = ["spp-ui-theme", "omp-omega-ui"] as const;

export type OmegaUiProviderProps = {
    children: ReactNode;
    /**
     * Доп. behavioral defaults поверх OMP defaults.
     * `renderPickerFullscreen` / `pickerMode` можно переопределить.
     */
    value?: UiProviderValue;
};

/**
 * App-level wiring `@omega/ui` для ompmobile.
 *
 * - вешает `.spp-ui-theme.omp-omega-ui` на `document.documentElement`
 *   (токены доступны и в portal/popover);
 * - задаёт `UiProvider` defaults: adaptive picker + MobileWindowFrame-like fullscreen.
 *
 * Component CSS подтягивается side-effect импортами модулей `@omega/ui`
 * (отдельный `@omega/ui/styles.css` не обязателен).
 */
export function OmegaUiProvider({ children, value }: OmegaUiProviderProps) {
    useLayoutEffect(() => {
        const root = document.documentElement;
        for (const className of THEME_ROOT_CLASSES) {
            root.classList.add(className);
        }
        return () => {
            for (const className of THEME_ROOT_CLASSES) {
                root.classList.remove(className);
            }
        };
    }, []);

    const providerValue = useMemo<UiProviderValue>(
        () => ({
            pickerMode: "auto",
            fullscreenMediaQuery: "(max-width: 640px), (pointer: coarse)",
            renderPickerFullscreen: renderOmpPickerFullscreen,
            ...value,
        }),
        [value],
    );

    return <UiProvider value={providerValue}>{children}</UiProvider>;
}
